import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Loading } from '@/components/common/Loading';
import type { Role, RoleFilters, RoleStatus, RoleType } from '../types';

interface RolesDataTableProps {
  data: Role[];
  columns: ColumnDef<Role>[];
  isLoading: boolean;
  filters: RoleFilters;
  updateFilter: <K extends keyof RoleFilters>(key: K, value: RoleFilters[K]) => void;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

const ROLE_STATUSES: RoleStatus[] = ['ACTIVE', 'ARCHIVED'];
const ROLE_STATUS_ACTIVE: Record<RoleStatus, string> = {
  ACTIVE: 'data-[state=on]:bg-emerald-500',
  ARCHIVED: 'data-[state=on]:bg-gray-500',
};
const ROLE_TYPES: RoleType[] = ['MANAGER', 'PARTNER', 'LEAD', 'STAFF'];

export function RolesDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  tableZoom: tableZoomProp,
}: RolesDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('roles');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
    status,
    type,
  } = filters;

  return (
    <div className="w-full">
      {/* Status pills */}
      <StatusFilterPills
        value={status}
        onChange={(v) => updateFilter('status', v as RoleStatus | undefined)}
        options={ROLE_STATUSES.map((s) => ({
          value: s,
          label: t(`status.${s}`),
          activeClassName: ROLE_STATUS_ACTIVE[s],
        }))}
      />
      <div className="flex flex-col-reverse sm:flex-row items-center gap-x-2 gap-y-2 py-4 flex-wrap">
        {/* Created Date Range Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-[240px] justify-between font-normal hover:bg-button-ground relative"
            >
              {minDate && maxDate
                ? `${format(new Date(minDate), 'yyyy-MM-dd', { locale: dateFnsLocale })} - ${format(
                    new Date(maxDate),
                    'yyyy-MM-dd',
                    { locale: dateFnsLocale }
                  )}`
                : t('createdDateRange')}
              <div className="flex items-center h-full absolute right-3 top-0">
                <Separator orientation="vertical" className="mx-3" />
                <CalendarIcon />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="range"
              selected={{
                from: minDate ? new Date(minDate) : undefined,
                to: maxDate ? new Date(maxDate) : undefined,
              }}
              onSelect={(range) => {
                updateFilter('createdAt.min', range?.from?.toISOString());
                updateFilter('createdAt.max', range?.to?.toISOString());
              }}
              numberOfMonths={2}
              captionLayout="dropdown"
            />
          </PopoverContent>
        </Popover>

        {/* Type Select */}
        <Select
          value={type ?? 'none'}
          onValueChange={(val) =>
            updateFilter('type', val === 'none' ? undefined : (val as RoleType))
          }
        >
          <SelectTrigger className="data-[placeholder]:text-black w-fit">
            <SelectValue placeholder={t('type')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('type')}</SelectItem>
            {ROLE_TYPES.map((tp) => (
              <SelectItem key={tp} value={tp}>
                {t(tp.toLowerCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Spacer */}
        <div className="relative max-w-sm min-w-32 ml-auto" />

        {/* Columns Visibility Dropdown */}
        {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
        <ColumnVisibility table={table} />
      </div>

      {/* Table */}
      <div className="border rounded-md">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={99}>
                  <Loading className="w-full" />
                </TableCell>
              </TableRow>
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 py-[100px] text-center">
                  <h1 className="text-xl font-medium mb-2">{t('noRolesFound')}</h1>
                  <p>{t('addRolesMessage')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
